<template lang="pug">
aside.main-menu(v-if="showMenu" :class="{ 'is-open': isMobileMenuOpen }")

    .d-flex.flex-column.min-vh-100
      button.main-menu__close(type="button" aria-label="Close menu" @click="closeMobileMenu")
        fai(type="fal" icon="times").size-24
      div.py-3.main-menu__brand(v-if="isOrgAdminActive")
       router-link(to='/manage/orgs' @click="resetActiveMenuItems").p-20
        img(src="@/assets/images/logos/bolt-rounded.png").size-50px
        img(src="@/assets/images/logos/logo-text@2x.png").w-170.h-30.pl-15
      div.py-3.main-menu__brand(v-else)
       router-link(to='/' @click="resetActiveMenuItems").p-20
        img(src="@/assets/images/logos/bolt-rounded.png").size-50px
        img(src="@/assets/images/logos/logo-text@2x.png").w-170.h-30.pl-15

      #nav.flex-grow-1.d-flex.flex-column.pos-r.main-menu-items(
        :class="{ 'is-collapsed': isMainMenuCollapsed }"
      )

        .px-20.mb-4
            orgs-dropdown

        el-menu(
          ref="menuRef"
          :default-active="activeMenuItem"
          :collapse="isMainMenuCollapsed"
          text-color="#303133"
          active-text-color="#303133"
          :unique-opened="true"
          @select="handleSelectMenu"
        ).flex-grow-1.d-flex.flex-column
          template(
            v-for="item in menuItems"
          )
            el-sub-menu(
              v-if="item.hasSubNav"
              :key="item.path"
              :index="item.path"
              )
              template(
                #title=''
              )
                div.d-flex.align-items-center
                  span(v-if='item.icon').d-inline-flex
                    fai(type="fal" :icon="item.icon" :class="{ 'icon-active': isMenuOpened(item) }").size-24
                  span.pl-15.fs-20 {{ item.label  }}
              el-menu-item-group
                el-menu-item(
                  v-for="submenu in item.subNav"
                  :index = "submenu.path"
                  :key="submenu.path"
                )
                  span.fs-18 {{ submenu.label }}
                  span(v-if='submenu.faIcon').submenu-right-icon
                    fai(type="fal" :icon="submenu.icon").size-18
            el-menu-item(
              v-else
              :key="item.path"
              :index="item.path"
              :class="isMenuActive(item.path)"
            )
              div.d-flex.align-items-center
                span(v-if='item.icon').d-inline-flex
                  fai(type="fal" :icon="item.icon" :class="{ 'icon-active': isMenuActive(item.path) }").size-24
                span.pl-15.fs-20 {{ item.label  }}
          div.mt-auto
            el-menu-item(
            v-if="!isOrgAdminActive"
            index="/settings"
            :class="isMenuActive('/settings')"
            )
              div.d-flex.align-items-center
                span.d-inline-flex
                  fai(type="fas" icon="gear").size-24
                span.pl-15.fs-20 Settings
            el-menu-item(@click='handleSignOut' index="/auth")
              div.d-flex.align-items-center
                span.d-inline-flex
                  fai(type="fas" icon="arrow-right-from-bracket").size-24
                span.pl-15.fs-20 Sign out
            hr.solid.my-4.mx-4
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, nextTick,
} from 'vue';
import { useStore, mapGetters } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import {
  ElMenu, ElMenuItem, ElMenuItemGroup, ElSubMenu, ElMessage,
} from 'element-plus';
import OrgsDropdown from '@/components/OrgsDropdown.vue';
import shared from '@/shared';
import orgShared from '@/views/manage-users-and-organizations/shared';
import store from '@/store';

import { dashboard as mainMenuItems, orgAdminMenu as orgMenuItems } from '@/data/main-menu';
import type { MenuItem } from '@/data/main-menu';

const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

export default defineComponent({
  name: 'AppMainMenu',
  components: {
    ElMenu, ElMenuItem, ElMenuItemGroup, ElSubMenu, OrgsDropdown,
  },

  computed: {
    ...mapGetters('shared', ['isMainMenuCollapsed']),
  },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const toShow = ref<boolean[]>([]);
    const { isOrgAdmin } = shared();
    const isOrgAdminActive = ref(false);
    const menuItemsCounter = ref(0);
    const menuRef = ref(null);
    const activeMenuItem = ref<string | null>(route.path);

    const isMobileMenuOpen = computed(() => store.getters['shared/isMobileMenuOpen']);
    const closeMobileMenu = () => store.dispatch('shared/setMobileMenuOpen', false);

    const isAuthenticated = computed(() => store.getters['users/isMeSignedIn']);
    const isAdminUser = computed(() => store.getters['users/isAdmin']);
    const isOrgAdminUser = computed(() => store.getters['users/isOrgAdmin']);

    const showMenu = computed(() => {
      if (route.path.startsWith('/web')) {
        return false;
      }

      return isAuthenticated.value && (isAdminUser.value || isOrgAdminUser.value);
    });

    function resolveMenuOrgPaths(items: MenuItem[], isOrgMenu = false): MenuItem[] {
      const orgId = selectedOrganization.value?.id;
      return items.map((item) => {
        const i = { ...item };
        if (i.subNav?.length) {
          i.subNav = resolveMenuOrgPaths(i.subNav, isOrgMenu);
        }
        if (i.path.includes('ORG_ID')) {
          i.path = orgId
            ? i.path.replace('ORG_ID', orgId)
            : i.path;
        }
        if (isOrgMenu && i.label === 'Activity' && orgId) {
          i.path = `/activity/${orgId}`;
        }
        return i;
      });
    }

    const menuItems = computed(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- re-run when the counter changes
      menuItemsCounter.value;

      if (isOrgAdminUser.value) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties -- TODO(lint): move to a watcher
        isOrgAdminActive.value = true;
        return resolveMenuOrgPaths(orgMenuItems, true);
      }

      return mainMenuItems;
    });

    watch(
      () => route.params.orgId,
      (newOrgId) => {
        toShow.value = orgMenuItems.map((p) => {
          if (p.path.indexOf('ORG_ID') >= 0 && !newOrgId) {
            return false;
          }
          return true;
        });

        if (newOrgId && route.path.includes('/manage/orgs/')
              && (!selectedOrganization.value || selectedOrganization.value.id !== newOrgId)) {
          const { getOrganization } = orgShared();
          getOrganization(newOrgId).then((org) => {
            if (org) {
              store.dispatch('organizations/SET_SELECTED_ORGANIZATION', org);
              localStorage.setItem('selectedOrganization', JSON.stringify(org));
            }
          }).catch((err) => {
            console.error('Failed to fetch organization:', err);
          });
        }
      },
      { immediate: true },
    );

    watch(
      () => store.getters['organizations/GET_SELECTED_ORGANIZATION'],
      () => {
        menuItemsCounter.value++;
      },
    );

    // Watch for route changes to update active menu item
    watch(
      () => route.path,
      (newPath) => {
        if (newPath.includes('/sticker-collections')) {
          const orgId = selectedOrganization.value?.id
            || route.params.orgId;
          if (orgId) {
            activeMenuItem.value = `/manage/orgs/${orgId}/stickers`;
            return;
          }
        }
        activeMenuItem.value = newPath;
      },
      { immediate: true },
    );

    // Close the mobile drawer after navigating
    watch(
      () => route.fullPath,
      () => store.dispatch('shared/setMobileMenuOpen', false),
    );

    const isMenuActive = (vRoute: string): string => {
      if (route.path === vRoute) return 'is-active';
      return '';
    };
    // Highlight a parent only when the current route belongs to one of its sub items,
    // so sibling routes like /manage/consents don't light up the /manage (Users) group.
    const isMenuOpened = (item: MenuItem): boolean => (item.subNav || []).some(
      (sub) => route.path === sub.path || route.path.startsWith(`${sub.path}/`),
    );

    const handleSelectMenu = (key: string): void => {
      if (key.includes('ORG_ID')) {
        ElMessage.warning('Select an organization from the dropdown at the top of the menu first.');
        return;
      }
      if (route.path === key) return;
      router.push({ path: key });
    };

    const goBack = () => {
      router.go(-1);
    };
    const handleSignOut = () => {
      localStorage.clear();
      store.dispatch('organizations/SET_SELECTED_ORGANIZATION', null);
      store.dispatch('users/resetMe');
      router.push('/auth');
    };

    const resetActiveMenuItems = () => {
      activeMenuItem.value = null;
      nextTick(() => {
        try {
          const openedSubMenuTitles = document.querySelectorAll('.el-sub-menu.is-opened .el-sub-menu__title');
          openedSubMenuTitles.forEach((title) => {
            // Simulate click to toggle submenu closed
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
            });
            title.dispatchEvent(clickEvent);
          });
          const activeMenuItems = document.querySelectorAll('.el-menu-item.is-active');
          activeMenuItems.forEach((item) => {
            item.classList.remove('is-active');
          });
        } catch (error) {
          // menu elements may not be rendered yet
        }
      });
    };

    return {
      activeMenuItem,
      isOrgAdmin,
      toShow,
      menuItems,
      showMenu,
      isMobileMenuOpen,
      closeMobileMenu,
      isOrgAdminActive,
      isAuthenticated,
      isAdminUser,
      isOrgAdminUser,
      isMenuActive,
      isMenuOpened,
      handleSelectMenu,
      handleSignOut,
      goBack,
      resetActiveMenuItems,
      menuRef,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';

// Color Variables - Modern Design System
$color-purple-text: #4349dd;
$color-purple-light: #f0f1ff;
$color-purple-hover: #3840c7;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-800: #1f2937;
$gray-900: #111827;

// Main Menu Container
.main-menu {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: $main-menu-width;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
  border-right: 1px solid $gray-200;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-collapsed {
    width: $main-menu-width-on-collapse;
  }
}

// Layout
.min-vh-100 {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

// Logo Section
.p-20 {
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  border-bottom: 1px solid $gray-200;
  background: white;
  transition: all 0.3s ease;

  &:hover {
    background: $gray-50;
  }

  a {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    text-decoration: none;
    width: 100%;
  }
}

.size-50px {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(67, 73, 221, 0.15);
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
}

.w-170 {
  width: 170px;
  object-fit: contain;
}

.h-30 {
  height: 30px;
}

// Menu Items Container
.main-menu-items {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.5rem 0;
  
  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 8px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: $gray-300;
    border-radius: 3px;
    transition: background 0.2s ease;

    &:hover {
      background: $gray-400;
    }
  }
}

.px-20 {
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

.mb-4 {
  margin-bottom: 1.25rem;
}

// Menu Styling
.el-menu {
  border-right: none;
  background: transparent;
  padding: 0 1rem;

  // Menu Items
  :deep(.el-menu-item) {
    height: 44px;
    line-height: 44px;
    border-radius: 10px;
    margin: 2px 0;
    padding: 0 1rem !important;
    // Only colors fade. No movement on hover, so the row never shifts under the cursor.
    transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
    color: $gray-700;
    font-weight: 500;
    font-size: 0.9375rem;
    position: relative;
    overflow: hidden;

    // Active indicator bar. It leaves instantly and only animates in on the new item,
    // so the old item doesn't show a shrinking sliver during navigation.
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 10px;
      bottom: 10px;
      width: 3px;
      height: auto;
      background: $color-purple-text;
      border-radius: 0 3px 3px 0;
      transform: scaleY(0);
      transition: none;
    }

    // !important beats the global `.el-menu-item:hover` grey in plugins.scss
    &:hover {
      background-color: $gray-100 !important;
      color: $gray-900;
    }

    &.is-active,
    &.is-active:hover {
      background-color: $color-purple-light !important;
      color: $color-purple-text;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(67, 73, 221, 0.12);

      // Doubled class outranks the legacy `.main-menu .el-menu .el-menu-item.is-active::before`
      // in _custom.scss, which would otherwise repaint the bar as a faint teal block.
      &.is-active::before {
        top: 10px;
        right: auto;
        bottom: 10px;
        border-radius: 0 3px 3px 0;
        background: $color-purple-text;
        transform: scaleY(1);
        transition: transform 0.2s cubic-bezier(0.2, 0, 0, 1);
      }

      .size-24 {
        color: $color-purple-text;
      }
    }

    .size-24 {
      transition: color 0.15s ease;
    }
  }

  // Submenu Styling
  :deep(.el-sub-menu) {
    .el-sub-menu__title {
      height: 44px;
      line-height: 44px;
      border-radius: 10px;
      margin: 2px 0;
      padding: 0 1rem !important;
      transition: background-color 0.15s ease, color 0.15s ease;
      color: $gray-700;
      font-weight: 500;
      font-size: 0.9375rem;

      &:hover {
        background-color: $gray-100 !important;
        color: $gray-900;
      }

      .size-24 {
        transition: color 0.15s ease;
      }
    }

    &.is-active.is-opened {
      > .el-sub-menu__title {
        background-color: $color-purple-light !important;
        color: $color-purple-text;
        font-weight: 600;

        .size-24 {
          color: $color-purple-text;
        }
      }
    }

    // Submenu items
    .el-menu-item-group {
      .el-menu-item {
        padding-left: 3.5rem !important;
        font-size: 0.875rem;
        height: 40px;
        line-height: 40px;

        &::before {
          display: none;
        }

        &.is-active,
        &.is-active:hover {
          background-color: $color-purple-light !important;
          color: $color-purple-text;
          font-weight: 600;
          position: relative;

          &::after {
            content: '';
            position: absolute;
            left: 2.5rem;
            top: 50%;
            transform: translateY(-50%);
            width: 6px;
            height: 6px;
            background: $color-purple-text;
            border-radius: 50%;
          }
        }
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .el-menu :deep(.el-menu-item),
  .el-menu :deep(.el-menu-item::before),
  .el-menu :deep(.el-sub-menu__title) {
    transition: none !important;
  }
}

// Icon Sizes
.size-24 {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.size-18 {
  width: 18px;
  height: 18px;
}

.icon-active {
  color: $color-purple-text;
}

// Spacing
.pl-15 {
  padding-left: 0.875rem;
}

.fs-20 {
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: -0.01em;
}

.fs-18 {
  font-size: 0.875rem;
  font-weight: 500;
}

.submenu-right-icon {
  margin-left: auto;
  opacity: 0.6;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}

// Bottom Section (Settings & Sign Out)
.mt-auto {
  margin-top: auto;
  padding: 1.25rem 0 1rem;
  border-top: 1px solid $gray-200;
  background: linear-gradient(180deg, transparent 0%, rgba(249, 250, 251, 0.5) 100%);

  .el-menu-item {
    color: $gray-600;
    font-size: 0.875rem;

    &:hover {
      background: $gray-100;
      color: $gray-900;
    }

    &.is-active {
      background: $color-purple-light;
      color: $color-purple-text;
    }
  }
}

// Divider
.solid {
  border: none;
  border-top: 1px solid $gray-200;
  margin: 0.75rem 0;
  opacity: 0.6;
}

.my-4 {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.mx-4 {
  margin-left: 1rem;
  margin-right: 1rem;
}

// Responsive Design
@media screen and (max-width: 1024px) {
  .main-menu {
    width: $main-menu-width-responsive;

    // Brand must fit the narrower rail instead of spilling over the content
    &__brand {
      a { padding: 1rem; gap: 0.5rem; }
      img:first-child { width: 36px; height: 36px; flex-shrink: 0; }
      img:last-child { width: auto; min-width: 0; max-width: 120px; height: auto; padding-left: 0; }
    }
  }
}

.main-menu__close { display: none; }

@media screen and (max-width: 768px) {
  .main-menu__close { display: flex; }

  .main-menu {
    width: min($main-menu-width, 85vw);
    height: 100dvh;
    transform: translateX(-100%);
    overflow-y: auto;
    overscroll-behavior: contain;

    &.is-open {
      transform: translateX(0);
      box-shadow: 4px 0 24px rgba(0, 0, 0, 0.18);
    }

    &__close {
      position: absolute;
      top: 12px;
      right: 8px;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border: 0;
      background: transparent;
      color: #303133;
    }

    // Compact brand so it clears the close button
    &__brand {
      padding-top: 0.5rem !important;
      padding-bottom: 0.25rem !important;

      a { padding: 12px 56px 12px 20px; display: flex; align-items: center; }
      img:first-child { width: 36px; height: 36px; }
      img:last-child { width: 120px; height: auto; padding-left: 10px; }
    }

    // The desktop icon rail does not apply to the drawer
    .main-menu-items,
    .main-menu-items .el-menu {
      width: 100% !important;
    }

    .min-vh-100 {
      height: auto;
      min-height: 100dvh;
    }
  }
}

// Animation for menu transitions
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.el-menu-item,
.el-sub-menu__title {
  animation: slideIn 0.3s ease-out backwards;
  
  @for $i from 1 through 20 {
    &:nth-child(#{$i}) {
      animation-delay: #{$i * 0.03}s;
    }
  }
}
</style>
